import PropTypes from 'prop-types';
import { C as CBase } from "../config/theme";
import { S } from "../config/theme";
import { useOrgSettings } from "../hooks/OrgSettingsContext";
import { useWindowWidth } from "../hooks/useWindowWidth";

export function FilterBar({
  primaryColor, allData, eventOverrides,
  filterEvent, setFilterEvent,
  filterRep, setFilterRep,
  filterBooth, setFilterBooth,
  filterTeam, setFilterTeam,
  qualFilters, setQualFilters,
  datePreset, setDatePreset,
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  showMoreFilters, setShowMoreFilters,
  onClear, theme, filteredCount,
}) {
  const w = useWindowWidth();
  const T = theme;
  const { rankings, teams, boothTypes, visibleDatePresets, qualificationFields, getRankingField } = useOrgSettings();
  const PC = primaryColor || CBase.primary;
  const C = CBase;

  const events = [...new Set([
    ...allData.map(r => r["Primary Campaign Source"]).filter(Boolean),
    ...Object.keys(eventOverrides || {}),
  ])].sort();
  const reps = [...new Set(allData.map(r => r["Sales Executive"]).filter(Boolean))].sort();

  const qualFilterFields = (qualificationFields || []).filter(f =>
    f.is_ranking_field || f.show_on_dashboard
  ).sort((a, b) => (a.dashboard_position || 99) - (b.dashboard_position || 99));

  const setQualFilter = (fieldId, val) => setQualFilters(prev => ({ ...prev, [fieldId]: val }));

  const activeQualCount = Object.values(qualFilters || {}).filter(v => v && v !== "all").length;
  const secondaryCount = activeQualCount + (filterBooth !== "all" ? 1 : 0) + (filterRep !== "all" ? 1 : 0);
  const hasDateFilter = datePreset !== "all";
  const hasAnyFilter = filterEvent !== "all" || filterTeam !== "all" || hasDateFilter || secondaryCount > 0;

  const selStyle = (isActive) => ({
    ...S.select(w),
    background: isActive ? `${PC}22` : T.inputBg,
    color: T.textBold,
    border: `1px solid ${isActive ? PC + "66" : T.border}`,
  });

  const active = [];
  if (datePreset !== "all" && !(datePreset === "custom" && !dateFrom && !dateTo)) {
    const label = datePreset === "custom" ? `${dateFrom || "start"} - ${dateTo || "now"}` : visibleDatePresets.find(p => p.value === datePreset)?.label || datePreset;
    active.push({ label, clear: () => { setDatePreset("all"); setDateFrom(""); setDateTo(""); } });
  }
  if (filterEvent !== "all") active.push({ label: filterEvent, clear: () => setFilterEvent("all") });
  if (filterTeam !== "all") active.push({ label: filterTeam, clear: () => setFilterTeam("all") });
  if (filterRep !== "all") active.push({ label: filterRep, clear: () => setFilterRep("all") });
  if (filterBooth !== "all") active.push({ label: filterBooth, clear: () => setFilterBooth("all") });
  Object.entries(qualFilters || {}).forEach(([fieldId, val]) => {
    if (!val || val === "all") return;
    const field = (qualificationFields || []).find(f => f.id === fieldId);
    const fieldLabel = field?.label || fieldId;
    const optLabel = field?.options ? (field.options.find(o => o.value === val)?.label || val) : val;
    active.push({ label: `${fieldLabel}: ${optLabel}`, clear: () => setQualFilter(fieldId, "all") });
  });

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: w < 640 ? 6 : 10, marginBottom: (showMoreFilters || datePreset === "custom") ? 0 : 8, flexWrap: w < 640 ? "wrap" : "nowrap" }}>
        <select style={{ ...selStyle(hasDateFilter), width: w < 640 ? "100%" : 180, flex: w < 640 ? "1 1 45%" : "0 0 180px" }}
          value={datePreset} onChange={e => { setDatePreset(e.target.value); if (e.target.value !== "custom") { setDateFrom(""); setDateTo(""); } }}>
          {visibleDatePresets.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {events.length > 0 && (
          <select style={{ ...selStyle(filterEvent !== "all"), width: w < 640 ? "100%" : 220, flex: w < 640 ? "1 1 45%" : "0 0 220px" }}
            value={filterEvent} aria-label="Filter by event" onChange={e => setFilterEvent(e.target.value)}>
            <option value="all">All Events</option>
            {events.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
        <select style={{ ...selStyle(filterTeam !== "all"), width: w < 640 ? "100%" : 200, flex: w < 640 ? "1 1 45%" : "0 0 200px" }}
          value={filterTeam} aria-label="Filter by team" onChange={e => setFilterTeam(e.target.value)}>
          <option value="all">All Account Teams</option>
          {teams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
        <button onClick={() => setShowMoreFilters(p => !p)}
          style={{ display: "flex", alignItems: "center", gap: 6, background: showMoreFilters ? T.inputBg : "transparent", border: `1px solid ${showMoreFilters ? PC + "44" : C.stone + "44"}`, borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: showMoreFilters ? PC : "#B5B9BC", cursor: "pointer", fontFamily: "'Inter',sans-serif", whiteSpace: "nowrap", flex: "0 0 auto" }}>
          + More Filters
          {secondaryCount > 0 && <span style={{ background: PC, color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 900 }}>{secondaryCount}</span>}
        </button>
        <div style={{ flex: 1 }} />
        {hasAnyFilter && <button onClick={onClear} style={{ background: "none", border: "none", color: PC, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif", whiteSpace: "nowrap" }} aria-label="Clear all filters">Clear all</button>}
      </div>

      {datePreset === "custom" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em" }}>From:</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ background: T.inputBg, color: T.textBold, border: `1px solid ${dateFrom ? PC + "66" : T.border}`, borderRadius: 6, padding: "7px 12px", fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none" }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em" }}>To:</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ background: T.inputBg, color: T.textBold, border: `1px solid ${dateTo ? PC + "66" : T.border}`, borderRadius: 6, padding: "7px 12px", fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none" }} />
        </div>
      )}

      {showMoreFilters && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 18px", marginTop: 8, marginBottom: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: w < 640 ? "1fr 1fr" : "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Sales Exec</div>
              <select value={filterRep} aria-label="Filter by rep" onChange={e => setFilterRep(e.target.value)} style={{ ...selStyle(filterRep !== "all"), width: "100%", fontSize: 12, padding: "7px 10px" }}>
                <option value="all">All</option>
                {reps.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Booth</div>
              <select value={filterBooth} aria-label="Filter by booth" onChange={e => setFilterBooth(e.target.value)} style={{ ...selStyle(filterBooth !== "all"), width: "100%", fontSize: 12, padding: "7px 10px" }}>
                <option value="all">All</option>
                {boothTypes.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            {qualFilterFields.map(field => {
              const currentVal = (qualFilters || {})[field.id] || "all";
              const isActive = currentVal !== "all";
              if (field.type === "yes_no") {
                return (
                  <div key={field.id}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{field.label}</div>
                    <select value={currentVal} onChange={e => setQualFilter(field.id, e.target.value)} style={{ ...selStyle(isActive), width: "100%", fontSize: 12, padding: "7px 10px" }}>
                      <option value="all">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                );
              }
              if (field.type === "dropdown" || field.type === "multi_select" || field.is_ranking_field) {
                return (
                  <div key={field.id}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{field.label}</div>
                    <select value={currentVal} onChange={e => setQualFilter(field.id, e.target.value)} style={{ ...selStyle(isActive), width: "100%", fontSize: 12, padding: "7px 10px" }}>
                      <option value="all">All</option>
                      {(field.options || []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.short_label || opt.label}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16, marginTop: 8, alignItems: "center" }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>Filtered:</span>
          {active.map((f, i) => (
            <div key={i} role="group" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${PC}18`, border: `1px solid ${PC}44`, borderRadius: 20, padding: "4px 10px 4px 12px", fontSize: 12, fontWeight: 700, color: PC }}>
              {f.label}
              <button type="button" onClick={f.clear} aria-label={`Remove filter: ${f.label}`} title={`Remove filter: ${f.label}`}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, lineHeight: 1, color: PC, opacity: 0.7, fontFamily: "inherit" }}>✕</button>
            </div>
          ))}
          {filteredCount !== undefined && <span style={{ fontSize: 11, color: T.textSec, marginLeft: 4 }}>({filteredCount} of {allData.length} leads)</span>}
        </div>
      )}
    </>
  );
}

FilterBar.propTypes = {
  primaryColor:    PropTypes.string,
  allData:         PropTypes.array.isRequired,
  eventOverrides:  PropTypes.object,
  filterEvent:     PropTypes.string,
  setFilterEvent:  PropTypes.func,
  filterRep:       PropTypes.string,
  setFilterRep:    PropTypes.func,
  onClear:         PropTypes.func,
  theme:           PropTypes.object.isRequired,
  filteredCount:   PropTypes.number,
};
